import {
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import TranslatableTextField from "components/TranslatableTextField";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import * as React from "react";
import tasksService from "services/tasks-service";
import usersService from "services/users-service";
import Attachments from "./Attachments";
import EmailInput from "#components/EmailsInput/index.js";

export default function TasksEditNewDialog({ isOpen, onSubmit, data, type }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [task, setTask] = React.useState({
    title: { en: "" },
    actionToTake: { en: "" },
    actionDetails: { en: "" },
    attachments: [],
  });
  const [options, setOptions] = React.useState({
    priorities: null,
    users: null,
    statuses: null,
  });
  const { label } = useLanguage();

  async function getOptions() {
    const [prioritiesResponse, usersResponse, statusesResponse] =
      await Promise.all([
        tasksService.getTaskPriorities(),
        usersService.getUsers({ view: "minimal" }),
        tasksService.getTaskStatuses(),
      ]);

    if (usersResponse[0].ok) {
      setOptions((options) => ({
        ...options,
        users: usersResponse[1].map((item) => {
          return {
            id: item.id,
            label: item.name,
          };
        }),
      }));
    }

    if (prioritiesResponse[0].ok) {
      setOptions((options) => ({
        ...options,
        priorities: prioritiesResponse[1].map((item) => {
          return {
            id: item.id,
            label: item.name,
          };
        }),
      }));
    }

    if (statusesResponse[0].ok) {
      setOptions((options) => ({
        ...options,
        statuses: statusesResponse[1].map((item) => {
          return {
            id: item.id,
            label: item.name,
          };
        }),
      }));
    }
  }

  React.useEffect(() => {
    getOptions();
  }, []);

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
    setTask({
      ...data,
      actionToTake: data?.allLanguageActionToTake || { en: "" },
      title: data?.allLanguageTitle || { en: "" },
      actionDetails: data?.allLanguageActionDetails || { en: "" },
      dueDate: data?.dueDate ? dayjs(data?.dueDate) : null,
      attachments: data?.attachments || [],
    });
  }, [data, isOpen]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          try {
            const sentTask = {
              id: task.id,
              title: task.title,
              actionDetails: task.actionDetails,
              actionToTake: task.actionToTake,
              priorityId: task.priorityId,
              statusId: task.statusId,
              isValidated: task.isValidated,
              userId: task.userId,
              dueDate: task.dueDate?.toDate?.() || task.dueDate?.$d || null,
              remarks: task.remarks,
              notifiedEmails: task.notifiedEmails,
              attachments: task.attachments,
              imageRequired: task.imageRequired,
            };
            const result = await onSubmit(sentTask);
            if (result.success) {
              handleClose();
            }
          } finally {
            setLoading(false);
          }
        },
      }}
    >
      <DialogTitle>
        {type == "edit" ? label("edit-task") : label("create-task")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1} sx={{ pt: 1.5 }}>
          <Grid item xs={12}>
            <TranslatableTextField
              autoFocus
              required
              id="title"
              name="title"
              label={label("title-singular")}
              type="text"
              values={task.title}
              onChange={(e) => {
                setTask({
                  ...task,
                  title: e.target.value,
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TranslatableTextField
              autoFocus
              required
              id="actionToTake"
              name="actionToTake"
              label={label("description-singular")}
              type="text"
              values={task.actionToTake}
              multiline
              minRows={3}
              onChange={(e) => {
                setTask({
                  ...task,
                  actionToTake: e.target.value,
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TranslatableTextField
              autoFocus
              id="details"
              name="details"
              label={label("additional-details")}
              type="text"
              values={task.actionDetails}
              multiline
              minRows={3}
              onChange={(e) => {
                setTask({
                  ...task,
                  actionDetails: e.target.value,
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <DropdownSelect
              fullWidth
              label={label("priority-singular")}
              loading={!options.priorities}
              items={options.priorities || []}
              required
              initialValue={task.priorityId || ""}
              onChange={(value) => {
                setTask({
                  ...task,
                  priorityId: value?.id,
                });
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <DropdownSelect
              fullWidth
              label={label("status-singular")}
              loading={!options.statuses}
              items={options.statuses || []}
              required
              initialValue={task.statusId || ""}
              onChange={(value) => {
                setTask({
                  ...task,
                  statusId: value?.id,
                });
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <DropdownSelect
              fullWidth
              label={label("assigned-to-singular")}
              loading={!options.users}
              items={options.users || []}
              required
              initialValue={task.userId || ""}
              onChange={(value) => {
                setTask({
                  ...task,
                  userId: value?.id,
                });
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={label("duedate-singular")}
                value={task.dueDate}
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    required: true,
                  },
                }}
                onChange={(e) => {
                  if (e)
                    setTask({
                      ...task,
                      dueDate: dayjs(e.$d),
                    });
                }}
                format="DD/MM/YYYY hh:mm A"
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={3}>
            <FormControlLabel
              label={label("is-validated")}
              sx={{ px: 2 }}
              control={
                <Checkbox
                  checked={!!task.isValidated}
                  onChange={(e) => {
                    setTask({
                      ...task,
                      isValidated: e.target.checked,
                    });
                  }}
                />
              }
            />
          </Grid>
          <Grid item xs={5}>
            <FormControlLabel
              label={"Image required"}
              sx={{ px: 2 }}
              control={
                <Checkbox
                  checked={!!task.imageRequired}
                  onChange={(e) => {
                    setTask({
                      ...task,
                      imageRequired: e.target.checked,
                    });
                  }}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              autoFocus
              id="remarks"
              name="remarks"
              label={label("remark-plural")}
              type="text"
              value={task.remarks}
              multiline
              minRows={2}
              onChange={(e) => {
                setTask({
                  ...task,
                  remarks: e.target.value,
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <EmailInput
              label="Notified emails (press Enter to add)"
              value={task.notifiedEmails || ""}
              onChange={(newValue) => {
                setTask((v) => {
                  let notifiedEmails;
                  if (typeof newValue == "function")
                    notifiedEmails = newValue(
                      v.notifiedEmails
                        ?.split(",")
                        .map((v) => v.trim())
                        .filter(Boolean) || [],
                    ).join(",");
                  else notifiedEmails = newValue.join(",");
                  return { ...v, notifiedEmails };
                });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.9rem", fontWeight: "400", mb: 1 }}
            >
              {label("attachment-plural")}
            </Typography>
            <Attachments
              attachments={task.attachments}
              onRemoveAttachment={(attachment, index) => {
                const newAttachments = [...(task.attachments || [])];
                newAttachments.splice(index, 1);

                setTask({ ...task, attachments: newAttachments });
              }}
              onAddAttachments={(files) => {
                setTask({
                  ...task,
                  attachments: [
                    ...(task.attachments || []),
                    ...files.map((url) => ({ url })),
                  ],
                });
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {type == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
